import { Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  BehaviorSubject,
  map,
  tap,
  of,
  delay,
  debounceTime,
  distinctUntilChanged,
  catchError,
  forkJoin,
  startWith,
} from "rxjs";
import { ValidationHistory } from "src/app/shared/models/ValidationHistory";
import { UpdateNoticeService } from "src/app/update-notice/service/updatenotice.service";
import { setLoader } from "src/app/utils/set-loaders";
import mock from "../../../assets/validation-history.json";
import { Breadcrumb } from "src/app/shared/breadcrumb/breadcrumb.component";
import { InfoManagementService } from "../service/info-management.service";

@Component({
  selector: "app-feature-validation-history",
  standalone: false,
  templateUrl: "./feature-validation-history.component.html",
  styleUrl: "./feature-validation-history.component.scss",
})
export class FeatureValidationHistoryComponent {
  private _activatedRoute = inject(ActivatedRoute);

  private _router = inject(Router);

  private _updateNoticeService = inject(UpdateNoticeService);

  private _infoMgmtService = inject(InfoManagementService);

  private _loaders$ = new BehaviorSubject({});

  updateNoticeId = this._activatedRoute.snapshot.params["unId"];

  featureId = this._activatedRoute.snapshot.params["featureId"];

  searchControl = new FormControl();

  columns = [
    {
      columnDef: "date",
      header: "Date",
      cell: (element: ValidationHistory) => `${element.date}`,
    },
    {
      columnDef: "result",
      header: "Result",
      cell: (element: ValidationHistory) => `${element.result}`,
    },
    {
      columnDef: "dataset",
      header: "Dataset",
      cell: (element: ValidationHistory) => `${element.dataset}`,
    },
    {
      columnDef: "version",
      header: "Version",
      cell: (element: ValidationHistory) => `${element.version}`,
    },
    {
      columnDef: "schema",
      header: "Profiles",
      cell: (element: ValidationHistory) => `${element.schema}`,
    },
    {
      columnDef: "bizrule",
      header: "Business Rules",
      cell: (element: ValidationHistory) => `${element.bizrule}`,
    },
  ];

  badgeIconLookup = {
    valid: {
      icon: "check_circle",
      color: "primary",
    },
    invalid: {
      icon: "error",
      color: "warn",
    },
    failed: {
      icon: "cancel",
      color: "warn",
    },
    pending: {
      icon: "schedule",
      color: "accent",
    },
  };

  loaders = toSignal(this._loaders$);

  headerData = toSignal(
    forkJoin({
      updateNotice: this._updateNoticeService.getUpdateNotice(this.updateNoticeId).pipe(catchError(() => of(null))),
      featureGmlName: this._infoMgmtService
        .getFeatureTypeGmlName(this.featureId, this.updateNoticeId)
        .pipe(catchError(() => of(null))),
    }).pipe(startWith(null))
  );

  updateNotice = computed(() => this.headerData()?.updateNotice);

  featureTypeGmlName = computed(() => this.headerData()?.featureGmlName);

  breadCrumb = computed(() => {
    const data = this.headerData();

    if (!data) return null;

    const breadCrumbEntries: Breadcrumb[] = [];
    const { updateNotice, featureGmlName } = data;

    if (updateNotice)
      breadCrumbEntries.push({
        id: "updateNotice",
        label: updateNotice.name,
        routerLink: this._router.createUrlTree(["/update/id", this.updateNoticeId]),
      });

    if (updateNotice && featureGmlName) {
      breadCrumbEntries.push({
        id: "featureGmlName",
        label: `${featureGmlName.featureType} : ${featureGmlName.gmlName}`,
        routerLink: this._router.createUrlTree([
          "/info-management",
          this.featureId,
          this.updateNoticeId,
          updateNotice.name,
          updateNotice.effectiveDate,
          true,
        ]),
      });
    }

    return breadCrumbEntries;
  });

  validationHistory = toSignal(
    of(mock)
      .pipe(delay(3000))
      .pipe(
        setLoader(this._loaders$, "validationHistory"),
        map((items: any[]) => items.map((item: any) => ValidationHistory.parse(item))),
        tap((result) => {
          console.log(result, "### VALIDATION HISTORY");
        })
      )
  );

  searchTerm = toSignal(this.searchControl.valueChanges.pipe(debounceTime(450), distinctUntilChanged()));

  backToForm(): void {
    const updateNotice = this.updateNotice();
    this._router.navigate([
      "/info-management",
      this.featureId,
      this.updateNoticeId,
      updateNotice.name,
      updateNotice.effectiveDate,
      true,
    ]);
  }
}
